import logger from '../utils/logger';

// Navigation service for client-side routing
class NavigationService {
  constructor() {
    this.listeners = [];
    this.currentPage = 'home';
    this.modalState = null; // Track modal state { listingId, title, slug }
  }

  // Add a listener for navigation changes
  addListener(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter((listener) => listener !== callback);
    };
  }

  // Navigate to a new page
  navigate(path, pageName) {
    // Update URL without page reload
    window.history.pushState({ page: pageName }, '', path);

    // Update current page
    this.currentPage = pageName;

    // Notify all listeners
    this.listeners.forEach((listener) => listener(pageName));
  }

  // Helper method to navigate by page name only (constructs path automatically)
  navigateTo(pageName) {
    const pathMap = {
      home: '/',
      'scene-builder': '/scene-builder',
      subscription: '/subscribe',
      'checkout-success': '/checkout/success',
      'checkout-cancel': '/checkout/cancel',
      account: '/account',
      orders: '/orders',
      scenes: '/scenes',
      gallery: '/gallery',
      'scene-viewer': '/scene-viewer',
      faq: '/faq',
      admin: '/admin',
      'admin-home': '/admin-home',
      'listing-admin': '/listing-admin',
      'color-audit': '/color-audit',
      'parallax-test': '/parallax',
    };

    const path = pathMap[pageName] || '/';
    this.navigate(path, pageName);
  }

  // Generate URL slug from title with smart hyphen cutoff
  generateSlug(title) {
    if (!title) return 'sticker';
    const maxLength = 30; // keep in sync with modal short title display
    const STOPWORDS = new Set([
      'and',
      'or',
      'with',
      'the',
      'a',
      'an',
      'of',
      'for',
      'to',
      'in',
      'on',
      'at',
    ]);

    const finalizeSlug = (slug) => {
      const s = (slug || '')
        .toLowerCase()
        .replace(/^-+|-+$/g, '')
        .replace(/-+/g, '-');
      const words = s.split('-').filter(Boolean);
      while (words.length > 0 && STOPWORDS.has(words[words.length - 1])) {
        words.pop();
      }
      return words.join('-') || 'sticker';
    };

    const appendVinylSuffix = (slug) => {
      const s = slug || '';
      if (s.endsWith('vinyl-decal')) return s;
      // Avoid duplicate trailing 'vinyl' or 'decal' tokens when appending
      const trimmed = s.replace(/(-vinyl)?(-decal)?$/, '');
      return `${trimmed}-vinyl-decal`;
    };

    let baseSlug = title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens

    // Remove common suffixes that don't add readability
    if (baseSlug.includes('-vinyl')) {
      baseSlug = baseSlug.replace(/-vinyl.*$/, ''); // Remove -vinyl and everything after
    } else if (baseSlug.includes('-sticker')) {
      baseSlug = baseSlug.replace(/-sticker.*$/, ''); // Remove -sticker and everything after
    } else if (baseSlug.includes('-decal')) {
      baseSlug = baseSlug.replace(/-decal.*$/, ''); // Remove -decal and everything after
    }

    // Clean up any trailing hyphens after suffix removal
    baseSlug = baseSlug.replace(/-+$/, '');

    // If already short enough, return as-is (after trimming trailing stopwords)
    if (baseSlug.length <= maxLength) {
      return appendVinylSuffix(finalizeSlug(baseSlug));
    }

    // Find the best hyphen cutoff point between 15-30 characters
    const minLength = 15;
    // maxLength constant above
    // Look for hyphens in the ideal range (15-30 chars), starting from max and working down
    for (let i = maxLength; i >= minLength; i--) {
      if (baseSlug[i] === '-') {
        return appendVinylSuffix(finalizeSlug(baseSlug.substring(0, i)));
      }
    }

    // If no hyphen found in range, cut at max length
    return appendVinylSuffix(finalizeSlug(baseSlug.substring(0, maxLength)));
  }

  // Open modal with URL routing
  openModal(listingData) {
    const slug = this.generateSlug(listingData.title);
    const path = `/${slug}`;

    this.modalState = {
      listingId: listingData.etsyListingId || listingData.listingId,
      title: listingData.title,
      slug: slug,
      data: listingData,
    };

    // Update URL and browser history
    window.history.pushState(
      {
        page: this.currentPage,
        modal: this.modalState,
      },
      '',
      path
    );

    // Notify listeners about modal state change
    this.listeners.forEach((listener) => listener(this.currentPage, this.modalState));
  }

  // Close modal and restore previous URL
  closeModal() {
    if (this.modalState) {
      this.modalState = null;

      // Go back to previous page (home)
      const homePath = this.currentPage === 'home' ? '/' : `/${this.currentPage}`;
      window.history.pushState({ page: this.currentPage }, '', homePath);

      // Notify listeners
      this.listeners.forEach((listener) => listener(this.currentPage, null));
    }
  }

  // Get current modal state
  getModalState() {
    return this.modalState;
  }

  // Handle browser back/forward buttons
  handlePopState(event) {
    if (event.state && event.state.modal) {
      this.modalState = event.state.modal;
    } else {
      this.modalState = null;
    }

    if (event.state && event.state.page) {
      this.currentPage = event.state.page;
    }

    // Notify listeners about state change
    this.listeners.forEach((listener) => listener(this.currentPage, this.modalState));
  }

  // Initialize deep linking from current URL
  async initializeFromURL(databaseService) {
    const currentPath = window.location.pathname;

    // Skip if we're on a known page route
    const pageRoutes = [
      '/',
      '/admin',
      '/admin-home',
      '/scene-builder',
      '/parallax',
      '/faq',
      '/listing-admin',
      '/gallery',
      '/scene-viewer',
      '/scenes',
      '/orders',
      '/account',
      '/subscribe',
      '/subscription',
      '/checkout/success',
      '/checkout/cancel',
      '/color-audit',
    ];
    if (pageRoutes.includes(currentPath)) {
      return null;
    }

    // Extract slug from path (e.g., "/trex" -> "trex")
    const slug = currentPath.replace(/^\//, '').replace(/\/$/, '');
    if (!slug) return null;

    try {
      // Search for sticker by title similarity to slug
      const stickerData = await this.findStickerBySlug(slug, databaseService);

      if (stickerData) {
        // Set modal state for the found sticker
        this.modalState = {
          listingId: stickerData.etsyListingId || stickerData.listingId,
          title: stickerData.title,
          slug: slug,
          data: stickerData,
        };

        // Update browser history with proper state
        window.history.replaceState(
          {
            page: this.currentPage,
            modal: this.modalState,
          },
          '',
          currentPath
        );

        return this.modalState;
      }
    } catch (error) {
      console.error('❌ Error initializing from URL:', error);
    }

    return null;
  }

  // Find sticker by slug using fuzzy matching
  async findStickerBySlug(slug, databaseService) {
    try {
      // Get all public stickers
      const response = await databaseService.getUnifiedStickers({
        publicVisible: true,
        limit: 999,
      });

      const stickers = Array.isArray(response)
        ? response
        : response?.data || response?.stickers || [];

      if (!Array.isArray(stickers) || stickers.length === 0) {
        console.warn('⚠️ No stickers found for slug matching');
        return null;
      }

      // Find exact slug match first
      let bestMatch = stickers.find((sticker) => {
        const stickerSlug = this.generateSlug(sticker.title);
        return stickerSlug === slug;
      });

      // If no exact match, try fuzzy matching
      if (!bestMatch) {
        const slugWords = slug.split('-').filter((word) => word.length > 2);
        const matches = stickers
          .map((sticker) => {
            const titleWords = sticker.title?.toLowerCase().split(/\s+/) || [];
            const matchCount = slugWords.filter((word) =>
              titleWords.some((titleWord) => titleWord.includes(word))
            ).length;

            return {
              sticker,
              score: matchCount / slugWords.length,
            };
          })
          .filter((match) => match.score > 0.5) // At least 50% match
          .sort((a, b) => b.score - a.score);

        bestMatch = matches.length > 0 ? matches[0].sticker : null;
      }

      if (bestMatch) {
        logger.log(`🔗 Deep link resolved: "${slug}" -> "${bestMatch.title}"`);
        return bestMatch;
      } else {
        console.warn(`⚠️ No sticker found for slug: "${slug}"`);
        return null;
      }
    } catch (error) {
      console.error('❌ Error finding sticker by slug:', error);
      return null;
    }
  }

  // Get current page
  getCurrentPage() {
    return this.currentPage;
  }
}

// Create singleton instance
const navigationService = new NavigationService();

export default navigationService;
